import React, { useEffect, useState, useRef } from 'react';
import logoImage from '../assets/logo.png';
import cachet from '../assets/cachet.png';

/**
 * Page d'impression de la liste des dépenses CGM uniquement.
 * Même style que État CGM (en-tête, logo, tableau) mais n'affiche que les dépenses.
 */
export default function DepensesCgmPrintPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [depenses, setDepenses] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [periodLabel, setPeriodLabel] = useState('');
  const [filterParams, setFilterParams] = useState(null);
  const printDoneRef = useRef(false);

  const formatMontant = (montant) => {
    const value = parseFloat(montant);
    if (isNaN(value)) return '0,000 TND';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const montantTronque = Math.floor(absValue * 1000 + 0.0000001) / 1000;
    const tempStr = montantTronque.toFixed(10);
    const parts = tempStr.split('.');
    let integerPart = parts[0] || '0';
    let decimalPart = parts[1] ? parts[1].substring(0, 3) : '000';
    if (decimalPart.length < 3) decimalPart = decimalPart.padEnd(3, '0');
    const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${isNegative ? '-' : ''}${integerFormatted},${decimalPart} TND`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${m}/${y}`;
  };

  const getCurrentDateLabel = (filterType, { date, mois, annee, dateDebut, dateFin }) => {
    if (filterType === 'periode' && dateDebut && dateFin) return `${dateDebut} - ${dateFin}`;
    if (filterType === 'jour' && date) return formatDate(date);
    if (filterType === 'mois' && mois && annee) return `${String(mois).padStart(2, '0')}/${annee}`;
    if (filterType === 'annee' && annee) return `${annee}`;
    return formatDate(new Date().toISOString());
  };

  const getTotalDepenses = (arr) =>
    arr.reduce((t, d) => t + parseFloat(d.montant || 0), 0);

  async function fetchDepenses({ filterType, date, mois, annee, dateDebut, dateFin }) {
    let bureauUrl = '/api/depenses/bureau';
    let clientCgmUrl = '/api/depenses';
    const clientCgmParams = new URLSearchParams();

    if (filterType === 'jour' && date) {
      bureauUrl = '/api/depenses/bureau/par-periode';
      bureauUrl += `?date_debut=${date}&date_fin=${date}`;
      clientCgmUrl = '/api/depenses/par-periode';
      clientCgmParams.append('date_debut', date);
      clientCgmParams.append('date_fin', date);
    } else if (filterType === 'periode' && dateDebut && dateFin) {
      bureauUrl = '/api/depenses/bureau/par-periode';
      bureauUrl += `?date_debut=${dateDebut}&date_fin=${dateFin}`;
      clientCgmUrl = '/api/depenses/par-periode';
      clientCgmParams.append('date_debut', dateDebut);
      clientCgmParams.append('date_fin', dateFin);
    } else if (filterType === 'mois' && mois && annee) {
      const start = `${annee}-${String(mois).padStart(2, '0')}-01`;
      const endDate = new Date(annee, mois, 0);
      const end = `${annee}-${String(mois).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      bureauUrl = '/api/depenses/bureau/par-periode';
      bureauUrl += `?date_debut=${start}&date_fin=${end}`;
      clientCgmUrl = '/api/depenses/par-periode';
      clientCgmParams.append('date_debut', start);
      clientCgmParams.append('date_fin', end);
    } else if (filterType === 'annee' && annee) {
      bureauUrl = '/api/depenses/bureau/par-periode';
      bureauUrl += `?date_debut=${annee}-01-01&date_fin=${annee}-12-31`;
      clientCgmUrl = '/api/depenses/par-periode';
      clientCgmParams.append('date_debut', `${annee}-01-01`);
      clientCgmParams.append('date_fin', `${annee}-12-31`);
    }

    if (clientCgmParams.toString()) clientCgmUrl += `?${clientCgmParams.toString()}`;

    const [bureauRes, clientCgmRes] = await Promise.all([
      fetch(bureauUrl),
      fetch(clientCgmUrl),
    ]);

    const bureauData = await bureauRes.json();
    const clientCgmData = await clientCgmRes.json();

    let depensesBureau = bureauData.success ? (bureauData.depenses || []) : [];
    let depensesClientCgm = clientCgmData.success ? (clientCgmData.depenses || []) : [];
    depensesClientCgm = depensesClientCgm.filter((dep) => {
      const desc = (dep.description || dep.libelle || '').toUpperCase();
      return desc.includes('[CGM]');
    });

    let data = [...depensesBureau, ...depensesClientCgm];
    data = data.filter((dep) => {
      const desc = (dep.description || dep.libelle || '').toUpperCase();
      const isHonoraireRecu = desc.includes('HONORAIRES REÇU') || desc.includes('HONORAIRES RECU');
      const isAvanceDeclaration = desc.includes('AVANCE');
      const isCarteBancaire = desc.includes('[CARTE BANCAIRE]') || desc.includes('[CARTE]');
      return !isHonoraireRecu && !isAvanceDeclaration && !isCarteBancaire;
    });

    return data;
  }

  function doPrint(depensesData, filterType, date, mois, annee, dateDebut, dateFin) {
    if (printDoneRef.current) return;
    printDoneRef.current = true;

    const now = new Date();
    const dateImpression = now.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const heureImpression = now.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    const infoGauche = `
    Matricule fiscale: 1408941/H/A/P/000
    <br>Banque Zitouna Agence Ben Arous
    <br>RIB : 25047000000039870129
`;
    const infoDroit = `
    Membre de la compagnie des comptables de Tunisie.
    <br>62 Avenue de France, bureau N°3, 1er étage 2013 Ben Arous.
    <br>(+216) 79 410 028
    <br>contact@ccgm.com.tn
`;

    const periodLabel = getCurrentDateLabel(filterType, {
      date,
      mois,
      annee,
      dateDebut,
      dateFin,
    });
    const totalDepenses = getTotalDepenses(depensesData);

    const rowsHtml =
      depensesData.length > 0
        ? depensesData.map((d) => {
            const rawText = (d.description || d.libelle || '').toUpperCase();
            const isCgmDepense = rawText.includes('[CGM]') || rawText.includes('HONORAIRES REÇU');
            let clientName = isCgmDepense
              ? d.nom_beneficiaire || d.beneficiaire || d.client || ''
              : d.beneficiaire || d.client || '';
            const clientFullName = ((d.client_nom || '') + ' ' + (d.client_prenom || '')).trim();
            if (clientFullName) clientName = clientFullName;
            let libelleText = d.description || d.libelle || '-';
            libelleText = libelleText.replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
            return `
          <tr>
            <td>${formatDate(d.date || d.date_operation)}</td>
            <td>${libelleText}</td>
            <td>${clientName || '-'}</td>
            <td>${formatMontant(d.montant)}</td>
          </tr>
        `;
          }).join('')
        : `<tr><td colspan="4" style="text-align:center;color:#666;font-style:italic;">Aucune dépense pour la période sélectionnée</td></tr>`;

    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Liste des dépenses CGM</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 12px; font-size: 11px; }
    .header { text-align: center; margin-bottom: 10px; }
    .logo-container img { height: 50px; width: auto; margin-bottom: 4px; }
    .header-infos { display: flex; justify-content: space-between; text-align: left; margin-top: 6px; font-size: 9px; line-height: 1.3; }
    .info-gauche { width: 48%; text-align: left; }
    .info-droit { width: 48%; text-align: right; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; border: 1px solid #000; }
    th, td { border: 1px solid #000; padding: 4px 6px; }
    thead th { font-weight: bold; background: #f9f9f9; border: 1px solid #000; }
    .details-section { margin-top: 12px; }
    .total-row { font-weight: bold; background: #f9f9f9; }
    .footer-section { margin-top: 20px; }
    .footer-content { display: flex; justify-content: space-around; align-items: flex-end; border-top: 1px solid #333; padding-top: 12px; margin-top: 12px; }
    .signature-block { text-align: center; }
    .signature-block img { height: 70px; width: auto; object-fit: contain; }
    .signature-line { font-size: 11px; color: #333; margin-top: 4px; }
  </style>
</head>
<body>

  <div style="text-align: right; margin-bottom: 10px; font-size: 11px; font-weight: bold;">
    Date d'impression : ${dateImpression}, ${heureImpression}
  </div>

  <div class="header">
    <div class="logo-container">
      <img src="${logoImage}" alt="Logo CGM" />
    </div>
    <div class="header-infos">
      <div class="info-gauche">${infoGauche}</div>
      <div class="info-droit">${infoDroit}</div>
    </div>
  </div>
  <hr>

  <div style="margin-bottom: 10px; font-weight: bold; font-size: 14px;">
    Liste des dépenses CGM — Période : ${periodLabel}
  </div>

  <div class="details-section">
    <table>
      <thead>
        <tr><th>Date</th><th>Libellé</th><th>Client</th><th>Montant</th></tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
      <tfoot>
        <tr class="total-row"><td colspan="3">TOTAL DÉPENSES</td><td>${formatMontant(totalDepenses)}</td></tr>
      </tfoot>
    </table>
  </div>

  <div class="footer-section">
    <div class="footer-content">
      <div class="signature-block"><img src="${cachet}" alt="Cachet et Signature" /><div class="signature-line">Cachet et Signature</div></div>
      <div class="signature-block"><div style="width:200px;height:60px;border-bottom:1px solid #000;margin:0 auto 5px;"></div><div class="signature-line">Signature Caissier</div></div>
    </div>
  </div>

</body>
</html>`;

    try {
      document.open();
      document.write(printContent);
      document.close();
      window.onload = () => {
        window.print();
      };
    } catch (_) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(printContent);
        w.document.close();
        w.onload = () => {
          w.print();
          w.close();
        };
      }
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filterType = params.get('filterType') || 'jour';
    const date = params.get('date');
    const mois = params.get('mois');
    const annee = params.get('annee');
    const dateDebut = params.get('dateDebut');
    const dateFin = params.get('dateFin');
    const isPreview = params.get('preview') === '1';
    setPreviewMode(isPreview);
    setFilterParams({ filterType, date, mois, annee, dateDebut, dateFin });

    const run = async () => {
      try {
        const depensesData = await fetchDepenses({
          filterType,
          date,
          mois: mois ? parseInt(mois, 10) : null,
          annee: annee ? parseInt(annee, 10) : null,
          dateDebut,
          dateFin,
        });
        setDepenses(depensesData);
        setPeriodLabel(getCurrentDateLabel(filterType, {
          date,
          mois,
          annee,
          dateDebut,
          dateFin,
        }));
        setLoading(false);
        if (!isPreview) {
          setTimeout(
            () =>
              doPrint(depensesData, filterType, date, mois, annee, dateDebut, dateFin),
            100
          );
        }
      } catch (e) {
        setError(e.message || 'Erreur');
        setLoading(false);
      }
    };

    run();
  }, []);

  const handlePrintFromPreview = () => {
    window.print();
  };

  if (loading) return <div className="container py-5">Préparation{previewMode ? ' de la prévisualisation' : ' de l\'impression'}...</div>;
  if (error) return <div className="container py-5 text-danger">{error}</div>;

  if (previewMode && !loading && filterParams) {
    const totalDepenses = getTotalDepenses(depenses);
    const now = new Date();
    const dateImpression = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const heureImpression = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    return (
      <div className="container py-4 depenses-cgm-print-preview">
        <style>{`
          @media print {
            .depenses-cgm-no-print { display: none !important; }
          }
          .depenses-cgm-print-preview { font-family: Arial, sans-serif; font-size: 11px; }
          .depenses-cgm-print-preview table { width: 100%; border-collapse: collapse; margin: 8px 0; border: 1px solid #000; }
          .depenses-cgm-print-preview th, .depenses-cgm-print-preview td { border: 1px solid #000; padding: 4px 6px; }
          .depenses-cgm-print-preview thead th { font-weight: bold; background: #f9f9f9; }
          .depenses-cgm-print-preview .total-row { font-weight: bold; background: #f9f9f9; }
        `}</style>
        <div className="depenses-cgm-no-print mb-3 d-flex justify-content-between align-items-center">
          <button type="button" className="btn btn-primary" onClick={handlePrintFromPreview}>
            <i className="fas fa-print me-1"></i> Imprimer
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => window.close()}>
            Fermer
          </button>
        </div>
        <div style={{ textAlign: 'right', marginBottom: 10, fontSize: 11, fontWeight: 'bold' }}>
          Date d'impression : {dateImpression}, {heureImpression}
        </div>
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <div className="mb-2">
            <img src={logoImage} alt="Logo CGM" style={{ height: 50, width: 'auto' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 9, lineHeight: 1.3, textAlign: 'left' }}>
            <div style={{ width: '48%' }}>
              Matricule fiscale: 1408941/H/A/P/000<br />
              Banque Zitouna Agence Ben Arous<br />
              RIB : 25047000000039870129
            </div>
            <div style={{ width: '48%', textAlign: 'right' }}>
              Membre de la compagnie des comptables de Tunisie.<br />
              62 Avenue de France, bureau N°3, 1er étage 2013 Ben Arous.<br />
              (+216) 79 410 028<br />
              contact@ccgm.com.tn
            </div>
          </div>
        </div>
        <hr />
        <div style={{ marginBottom: 10, fontWeight: 'bold', fontSize: 14 }}>
          Liste des dépenses CGM — Période : {periodLabel}
        </div>
        <div>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Libellé</th>
                <th>Client</th>
                <th>Montant</th>
              </tr>
            </thead>
            <tbody>
              {depenses.length > 0 ? (
                depenses.map((d, i) => {
                  const rawText = (d.description || d.libelle || '').toUpperCase();
                  const isCgmDepense = rawText.includes('[CGM]') || rawText.includes('HONORAIRES REÇU');
                  let clientName = isCgmDepense ? (d.nom_beneficiaire || d.beneficiaire || d.client || '') : (d.beneficiaire || d.client || '');
                  const clientFullName = ((d.client_nom || '') + ' ' + (d.client_prenom || '')).trim();
                  if (clientFullName) clientName = clientFullName;
                  let libelleText = d.description || d.libelle || '-';
                  libelleText = libelleText.replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
                  return (
                    <tr key={i}>
                      <td>{formatDate(d.date || d.date_operation)}</td>
                      <td>{libelleText}</td>
                      <td>{clientName || '-'}</td>
                      <td>{formatMontant(d.montant)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                    Aucune dépense pour la période sélectionnée
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan="3">TOTAL DÉPENSES</td>
                <td>{formatMontant(totalDepenses)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={cachet} alt="Cachet et Signature" style={{ height: 70, width: 'auto', objectFit: 'contain' }} />
            <div style={{ fontSize: 11, color: '#333', marginTop: 4 }}>Cachet et Signature</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 200, height: 60, borderBottom: '1px solid #000', margin: '0 auto 5px' }} />
            <div style={{ fontSize: 11, color: '#333', marginTop: 4 }}>Signature Caissier</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
